import React, { Fragment, useEffect, useState } from "react";
import AddLabelModal from "../../components/modal/AddLabelModal"; // Adjust path
import EditLabelModal from "../../components/modal/EditLabelModal"; // Add this import
import Swal from "sweetalert2";
import { adminApi } from "../../repositories/admin";
import { EllipsisVerticalIcon, PencilIcon } from "@heroicons/react/24/outline"; // Importing icons
import { Menu, Transition } from "@headlessui/react";

interface Label {
  id: number;
  name: string;
  type: string;
  availableDSPs: string[];
}

const LabelPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false); 
  const [labels, setLabels] = useState<Label[]>([]);
  const [currentLabel, setCurrentLabel] = useState<Label | null>(null); 

  const fetchLabels = async () => {
    try {
      const response = await adminApi.getLabels();
      setLabels(response.data.data);
    } catch (error: any) {
      Swal.fire(
        "Error",
        error?.response?.data?.message || "Failed to fetch labels",
        "error"
      );
    }
  };

  const handleEdit = async (id: number) => {
    const labelToEdit = labels.find((label) => label.id === id);
    if (labelToEdit) {
      setCurrentLabel(labelToEdit);
      setIsEditModalOpen(true);
    }
  };

  const handleSaveEdit = async (name: string, type: string, availableDSPs: string[]) => {
    if (!currentLabel) {
      return Swal.fire("Error", "No label selected for editing", "error");
    }
    try {
      const {data} = await adminApi.updateLabel(currentLabel.id, {
        name,
        type,
        availableDSPs
      });
      if(data?.success) {
        Swal.fire("Success", "Label updated successfully", "success");
        setLabels((prev) => prev.map((label) => 
          label.id === currentLabel.id 
            ? { 
                ...label, 
                name, 
                type, 
                availableDSPs: availableDSPs 
              } 
            : label
        ));
      }
      setIsEditModalOpen(false);
    } catch (error: any) {
      Swal.fire(
        "Error",
        error?.response?.data?.message || "Failed to update label",
        "error"
      );
    } 
  };

  useEffect(() => {
    fetchLabels();
  }, []);

  return (
    <div className="min-h-screen p-6 rounded-lg shadow-lg bg-gray-50">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-700">Client Management</h1>
        <button
          onClick={() => setIsModalOpen(true)}
          className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
        >
          Add Company
        </button>
      </div>

      {labels.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-center text-gray-500">
          <h2 className="mb-2 text-xl font-semibold">No Labels Found</h2>
        </div>
      ) : (
        <table className="w-full bg-white rounded-lg shadow-md">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3 text-left border">Id</th>
              <th className="p-3 text-left border">Name</th>
              <th className="p-3 text-left border">Type</th>
              <th className="p-3 text-left border">DSP Access</th>
              <th className="p-3 text-left border">Actions</th>
            </tr>
          </thead>
          <tbody>
            {labels.map((label, index) => (
              <tr
                key={label.id}
                className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}
              >
                <td className="p-3 border">{label.id}</td>
                <td className="p-3 border">{label.name}</td>
                <td className="p-3 border">{label.type}</td>
                <td className="p-3 border">{label.availableDSPs?.join(", ")}</td>
                <td className="p-3 text-center border">
                  <Menu as="div" className="relative inline-block text-left">
                    <Menu.Button className="p-2 rounded-md hover:bg-gray-200">
                      <EllipsisVerticalIcon className="w-5 h-5" />
                    </Menu.Button>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 z-10 py-1 bg-white divide-y divide-gray-100 rounded-md shadow-lg min-w-32 -top-5">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => handleEdit(label.id)}
                              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 group hover:bg-gray-100"
                            >
                              <PencilIcon className="w-4 h-4 mr-2" /> Edit
                            </button>
                          )}
                        </Menu.Item>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Add Company Modal */}
      <AddLabelModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          fetchLabels();
        }}
      />

      {/* Edit Label Modal */}
      {currentLabel && (
        <EditLabelModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          labelId={currentLabel.id}
          initialName={currentLabel.name}
          initialType={currentLabel.type}
          initialAvailableDSPs={currentLabel.availableDSPs|| []}
          onSave={handleSaveEdit}
        />
      )}
    </div>
  );
};

export default LabelPage;